import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Building2, User } from 'lucide-react';

export default function RegisterPage() {
  const { t } = useLanguage();
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '', password: '', name: '',
    company: '', company_vat: '', company_address: '', company_phone: '', company_email: '',
    contact_name: '', contact_position: '', contact_phone: '', contact_email: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate('/portal'); }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        email: form.email, password: form.password, name: form.name,
        company: form.company,
        company_vat: form.company_vat, company_address: form.company_address,
        company_phone: form.company_phone, company_email: form.company_email,
        contact_name: form.contact_name, contact_position: form.contact_position,
        contact_phone: form.contact_phone, contact_email: form.contact_email,
      } as any);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const u = (field: string, val: string) => setForm(prev => ({ ...prev, [field]: val }));
  const inputStyle: React.CSSProperties = { width: '100%', padding: '11px 13px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'var(--font-body)' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 72, paddingBottom: 48, background: 'var(--bg-section)' }}>
      <div style={{ width: '100%', maxWidth: 640, padding: '0 24px' }}>
        <div style={{ background: 'white', border: '1px solid var(--border)', padding: '40px 36px' }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('auth.register')}</h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>Maluz Technologies - Client Portal</p>

          {error && (
            <div data-testid="register-error" style={{ padding: '10px 14px', background: 'rgba(211,47,47,0.08)', border: '1px solid var(--secondary)', color: 'var(--secondary)', marginBottom: 16, fontSize: 13 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} data-testid="register-form">
            {/* Account credentials */}
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={14} /> Account Credentials
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="reg-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>{t('auth.name')} *</label>
                  <input data-testid="register-name-input" value={form.name} onChange={e => u('name', e.target.value)} required placeholder="Full name" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('auth.email')} *</label>
                  <input data-testid="register-email-input" type="email" value={form.email} onChange={e => u('email', e.target.value)} required placeholder="your@email.com" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('auth.password')} *</label>
                  <input data-testid="register-password-input" type="password" value={form.password} onChange={e => u('password', e.target.value)} required placeholder="Min. 6 characters" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Company details */}
            <div style={{ marginBottom: 24, padding: 20, background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Building2 size={14} /> Company Details
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="reg-grid">
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input data-testid="register-company-input" value={form.company} onChange={e => u('company', e.target.value)} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>VAT / NIF</label>
                  <input data-testid="register-vat-input" value={form.company_vat} onChange={e => u('company_vat', e.target.value)} placeholder="Tax ID number" style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Company Address</label>
                  <input data-testid="register-address-input" value={form.company_address} onChange={e => u('company_address', e.target.value)} placeholder="Full address" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Company Phone</label>
                  <input data-testid="register-company-phone" value={form.company_phone} onChange={e => u('company_phone', e.target.value)} placeholder="+244..." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Company Email</label>
                  <input data-testid="register-company-email" type="email" value={form.company_email} onChange={e => u('company_email', e.target.value)} placeholder="info@company.com" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Contact Person */}
            <div style={{ marginBottom: 24, padding: 20, background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={14} /> Contact Person / Representative
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="reg-grid">
                <div>
                  <label style={labelStyle}>Contact Name</label>
                  <input data-testid="register-contact-name" value={form.contact_name} onChange={e => u('contact_name', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Position</label>
                  <input data-testid="register-contact-position" value={form.contact_position} onChange={e => u('contact_position', e.target.value)} placeholder="e.g. IT Manager" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Phone</label>
                  <input data-testid="register-contact-phone" value={form.contact_phone} onChange={e => u('contact_phone', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input data-testid="register-contact-email" type="email" value={form.contact_email} onChange={e => u('contact_email', e.target.value)} style={inputStyle} />
                </div>
              </div>
            </div>

            <button type="submit" data-testid="register-submit-btn" disabled={loading}
              style={{ width: '100%', padding: '14px', background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: loading ? 0.7 : 1, cursor: 'pointer' }}>
              {loading ? '...' : t('auth.register')}
            </button>
          </form>

          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {t('auth.has_account')} <Link to="/login" data-testid="login-link" style={{ color: 'var(--primary)', fontWeight: 600 }}>{t('auth.login')}</Link>
            </p>
          </div>
        </div>

        <style>{`
          @media (max-width: 500px) { .reg-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </div>
  );
}
